import React from 'react';
import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis, Cell } from 'recharts';
import { AlertTriangle } from 'lucide-react';

interface ComparisonChartProps {
  data: {
    name: string;
    value: number;
    isMain?: boolean;
  }[];
  title: string;
  unit?: string;
  className?: string;
  noDataMessage?: string;
}

const formatValue = (value: number, unit: string) => {
  if (value === undefined || value === null || isNaN(value)) return '0';

  if (unit === 'R$') {
    if (value >= 1000000) {
      return `${(value / 1000000).toLocaleString('pt-BR', {
        minimumFractionDigits: 1,
        maximumFractionDigits: 1
      })}mi`;
    }
    return value.toLocaleString('pt-BR', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    });
  } else if (unit === 'R$/m²') {
    if (value >= 1000) {
      return `${(value / 1000).toLocaleString('pt-BR', {
        minimumFractionDigits: 1,
        maximumFractionDigits: 1
      })}mil`;
    }
    return value.toLocaleString('pt-BR', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    });
  }
  
  return value.toLocaleString('pt-BR', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  });
};

const formatCurrencyValue = (value: number) => {
  if (value === undefined || value === null || isNaN(value)) return '0';
  return value.toLocaleString('pt-BR', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  });
};

const CustomTooltip = ({ active, payload, label, unit }: any) => {
  if (active && payload && payload.length) {
    const data = payload[0].payload;
    const value = payload[0].value;
    
    if (value === undefined || value === null || isNaN(value)) {
      return (
        <div className="glass p-3 rounded-md shadow-md">
          <p className="font-medium">{label}</p>
          <p className="text-blue-dark font-bold">Valor não disponível</p>
          {data.isMain && <p className="text-xs text-blue-medium">Imóvel Analisado</p>}
        </div>
      );
    }
    
    let formattedValue;
    if (unit === 'R$') {
      formattedValue = `R$ ${formatValue(value, unit)}`;
    } else if (unit === 'R$/m²') {
      formattedValue = `R$ ${formatValue(value, unit)}/m²`;
    } else {
      formattedValue = `${formatValue(value, unit)} ${unit || ''}`;
    }
    
    return (
      <div className="glass p-3 rounded-md shadow-md">
        <p className="font-medium">{label}</p>
        <p className="text-blue-dark font-bold">
          {formattedValue}
        </p>
        {data.isMain && <p className="text-xs text-blue-medium">Imóvel Analisado</p>}
      </div>
    );
  }
  return null;
};

const ComparisonChart: React.FC<ComparisonChartProps> = ({ 
  data, 
  title, 
  unit = '', 
  className = '',
  noDataMessage = 'Sem dados disponíveis'
}) => {
  const formatAxisTick = (value: number) => {
    if (value === undefined || value === null || isNaN(value)) {
      return '0';
    }
    
    if (unit === 'R$' || unit === 'R$/m²') {
      return formatValue(value, unit);
    }
    return formatValue(value, unit);
  };

  const sanitizedData = (data || []).map(item => ({
    ...item,
    value: (item.value === undefined || item.value === null || isNaN(item.value)) ? 0 : item.value
  }));

  const hasComparisonData = sanitizedData.some(item => !item.isMain && item.value > 0);
  
  const filteredData = sanitizedData.filter(item => item.isMain || item.value > 0);

  return (
    <div className={`p-4 bg-white rounded-lg shadow-sm ${className}`}>
      <h3 className="text-lg font-medium mb-4">{title}</h3>
      
      {filteredData.length > 0 ? (
        <div className="h-64">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={filteredData} barCategoryGap={12}>
              <CartesianGrid strokeDasharray="3 3" vertical={false} opacity={0.3} />
              <XAxis dataKey="name" axisLine={false} tickLine={false} />
              <YAxis 
                axisLine={false} 
                tickLine={false} 
                tickFormatter={formatAxisTick}
              />
              <Tooltip content={<CustomTooltip unit={unit} />} />
              <Bar 
                dataKey="value" 
                fill="#57C5B6" 
                radius={[4, 4, 0, 0]}
                animationDuration={1500}
              >
                {filteredData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.isMain ? '#1A5F7A' : '#57C5B6'} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      ) : (
        <div className="h-64 flex flex-col items-center justify-center text-gray-500">
          <AlertTriangle size={32} className="mb-2 text-amber-400" />
          <p>{noDataMessage}</p>
        </div>
      )}
      
      {!hasComparisonData && filteredData.length > 0 && (
        <div className="mt-4 text-sm text-gray-500 text-center">
          <p>Apenas o imóvel analisado está sendo exibido, pois não há dados comparativos disponíveis para esta região.</p>
        </div>
      )}
    </div>
  );
};

export default ComparisonChart;
